import { yupResolver } from '@hookform/resolvers/yup'
import { ErrorCodes, getMessage } from '@limitless-minds/app-messages'
import { Button } from '@limitless-minds/ui-button'
import { Checkbox } from '@limitless-minds/ui-checkbox'
import { Dropdown } from '@limitless-minds/ui-dropdown'
import { Input } from '@limitless-minds/ui-input'
import { Paragraph } from '@limitless-minds/ui-text'
import { Box, Stack, styled, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { timezones } from '@/data/api'
import { useRegister, useValidateEmailInvite } from '@/data/auth'
import { PATHS } from '@/router/constants'
import { useStore } from '@/store'
import { Error as IError } from '@/types/Error'
import ErrorMessage from '@/ui/atoms/ErrorMessage'
import HeaderWrapper from '@/ui/molecules/HeaderWrapper'

import { FIELDS, schemaStepThree } from './schema'

const StyledDiv = styled(Stack)(`
  justify-content: center;
  align-items: center;
  align-self: center;
  width: 100%;
  max-width: 25rem;
  height: 100%;
  gap: 2rem;
`)

interface StepThreeData {
  firstName: string
  lastName: string
  displayName: string
  timezone: string
  eulaAccepted: boolean
}

const StepThree: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { id: qrId } = useParams<{ id: string }>()
  const token = searchParams.get('token')
  const [failedRegisterMessage, setFailedRegisterMessage] = useState('')
  const {
    firstName,
    lastName,
    displayName,
    timezone,
    eulaAccepted,
    isDirectBrowserFlow,
    email,
    password,
    setFirstName,
    setLastName,
    setDisplayName,
    setTimezone,
    setEulaAccepted,
    setIsDirectBrowserFlow,
    increaseStep,
    skipStep,
  } = useStore()
  const { mutate: register } = useRegister()
  const { mutate: validateEmailInvite } = useValidateEmailInvite()

  useEffect(() => {
    if (qrId && isDirectBrowserFlow) {
      setIsDirectBrowserFlow(false)
    }
  }, [qrId, isDirectBrowserFlow, setIsDirectBrowserFlow])

  const methods = useForm<StepThreeData>({
    resolver: yupResolver(schemaStepThree),
    defaultValues: {
      [FIELDS.FIRST_NAME]: firstName,
      [FIELDS.LAST_NAME]: lastName,
      [FIELDS.DISPLAY_NAME]: displayName,
      [FIELDS.TIMEZONE]: timezone,
      [FIELDS.EULA_ACCEPTED]: eulaAccepted,
    },
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods

  const onSubmit: SubmitHandler<StepThreeData> = (data) => {
    const { firstName, lastName, displayName, timezone, eulaAccepted } = data
    setFirstName(firstName)
    setLastName(lastName)
    setDisplayName(displayName)
    setTimezone(timezone)
    setEulaAccepted(eulaAccepted)

    if (isDirectBrowserFlow) {
      increaseStep()
    } else {
      handleNoCode(data)
    }
  }

  const handleNoCode = (formData?: StepThreeData): void => {
    // Use form data if provided, otherwise use store values
    const currentFirstName = formData?.firstName || firstName
    const currentLastName = formData?.lastName || lastName
    const currentDisplayName = formData?.displayName || displayName
    const currentTimezone = formData?.timezone || timezone

    if (token) {
      validateEmailInvite(
        {
          body: {
            firstName: currentFirstName,
            lastName: currentLastName,
            displayName: currentDisplayName,
            timezone: currentTimezone,
            password,
            token,
          },
        },
        {
          onSuccess: () => {
            setFailedRegisterMessage('')
            useStore.persist.clearStorage()
            navigate(PATHS.LOGIN)
          },
          onError: (error) => {
            const errorResponse = error as IError
            const errorMessage = getMessage(
              errorResponse.response?.data?.error?.errorCode,
              errorResponse.response?.data?.error?.params
            )
            setFailedRegisterMessage(errorMessage)
          },
        }
      )
    } else {
      registerWithPayload(undefined, formData)
    }
  }

  const registerWithPayload = (extraBody?: { textCode?: string }, formData?: StepThreeData): void => {
    // Use form data if provided, otherwise use store values
    const currentFirstName = formData?.firstName || firstName
    const currentLastName = formData?.lastName || lastName
    const currentDisplayName = formData?.displayName || displayName
    const currentTimezone = formData?.timezone || timezone

    const body: {
      firstName: string
      lastName: string
      displayName: string
      timezone: string
      email: string
      password: string
      qrCodeId?: string
      textCode?: string
    } = {
      firstName: currentFirstName,
      lastName: currentLastName,
      displayName: currentDisplayName,
      timezone: currentTimezone,
      email,
      password,
    }

    if (qrId) {
      body.qrCodeId = qrId
    } else if (extraBody?.textCode) {
      body.textCode = extraBody.textCode
    }

    register(
      { body },
      {
        onSuccess: () => {
          setFailedRegisterMessage('')
          skipStep(2) // Go to StepFour
        },
        onError: (error) => {
          const errorResponse = error as IError
          const errorCode = errorResponse.response?.data?.error?.errorCode
          if (errorCode === ErrorCodes.SIGNUP.USER_IS_ALREADY_ACTIVE) {
            navigate(PATHS.LOGIN)
            return
          }
          const errorMessage = getMessage(errorCode, errorResponse.response?.data?.error?.params)
          setFailedRegisterMessage(errorMessage)
        },
      }
    )
  }

  return (
    <StyledDiv>
      <HeaderWrapper
        title={
          <>
            Welcome to Neumi, <span style={{ fontWeight: 400 }}>powered by</span>{' '}
            <span style={{ whiteSpace: 'nowrap' }}>Limitless Minds</span>
          </>
        }
        description={
          <>
            You’re getting access to the Neumi platform.
            <br />
            <br />
            <strong>Plus:</strong> Our Newsletter, The Limitless Minds Playbook
            <br />
            Monthly mindset tools, performance hacks, podcast highlights, and insights from world-class coaches.
          </>
        }
      />
      <Stack gap='2rem' width='100%'>
        {failedRegisterMessage !== '' && <ErrorMessage>{failedRegisterMessage}</ErrorMessage>}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap='2rem' width='100%' justifyContent='center' height='100%'>
              <Stack gap='0.75rem'>
                <Box
                  display='flex'
                  gap='0.5rem'
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      flexDirection: 'column',
                    },
                  }}
                >
                  <Controller
                    name={FIELDS.FIRST_NAME}
                    control={control}
                    defaultValue={firstName}
                    render={({ field }) => (
                      <Input
                        label={'First Name'}
                        {...field}
                        error={errors?.[FIELDS.FIRST_NAME] ? true : false}
                        errorMessage={errors?.[FIELDS.FIRST_NAME]?.message}
                      />
                    )}
                  />
                  <Controller
                    name={FIELDS.LAST_NAME}
                    control={control}
                    defaultValue={lastName}
                    render={({ field }) => (
                      <Input
                        label={'Last Name'}
                        {...field}
                        error={errors?.[FIELDS.LAST_NAME] ? true : false}
                        errorMessage={errors?.[FIELDS.LAST_NAME]?.message}
                      />
                    )}
                  />
                </Box>
                <Controller
                  name={FIELDS.DISPLAY_NAME}
                  control={control}
                  defaultValue={displayName}
                  render={({ field }) => (
                    <Stack gap='0.5rem'>
                      <Input
                        label={'Display Name'}
                        {...field}
                        error={errors?.[FIELDS.DISPLAY_NAME] ? true : false}
                        errorMessage={errors?.[FIELDS.DISPLAY_NAME]?.message}
                        topLabel='Choose Display Name'
                      />
                      <Paragraph variant='p16' color={theme.palette.role.onSurfaceMuted}>
                        Your display name will be used across LMXP&apos;s community features. You can change this later
                        in your Account Settings.
                      </Paragraph>
                    </Stack>
                  )}
                />
                <Controller
                  name={FIELDS.TIMEZONE}
                  control={control}
                  defaultValue={timezone}
                  render={({ field }) => (
                    <Dropdown
                      topLabel='Your timezone'
                      options={timezones}
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={errors?.[FIELDS.TIMEZONE] ? true : false}
                      errorMessage={errors?.[FIELDS.TIMEZONE]?.message}
                    />
                  )}
                />
              </Stack>
              <Controller
                name={FIELDS.EULA_ACCEPTED}
                control={control}
                defaultValue={eulaAccepted}
                render={({ field }) => {
                  return (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      error={!!errors?.[FIELDS.EULA_ACCEPTED]}
                      errorMessage={errors?.[FIELDS.EULA_ACCEPTED]?.message}
                      label={
                        <Paragraph variant='p16' color={theme.palette.role.onSurfaceVariant}>
                          I have read and agree to the{' '}
                          <a
                            href='https://limitlessminds.com/terms-and-conditions'
                            target='_blank'
                            style={{ textDecoration: 'underline', color: theme.palette.role.primary }}
                          >
                            terms and conditions
                          </a>
                        </Paragraph>
                      }
                    />
                  )
                }}
              />
              <Button type='submit' width='100%'>
                {isDirectBrowserFlow ? 'Continue' : 'Sign Up'}
              </Button>
            </Stack>
          </form>
        </FormProvider>
      </Stack>
    </StyledDiv>
  )
}

export default StepThree
